import { Moon, Sun, Monitor } from 'lucide-react'
import { cn } from '@/utils'
import { useTheme, type Theme } from '@/hooks/useTheme'

const options: Array<{ value: Theme; icon: typeof Sun; label: string }> = [
  { value: 'light', icon: Sun, label: 'Light' },
  { value: 'dark', icon: Moon, label: 'Dark' },
  { value: 'system', icon: Monitor, label: 'System' },
]

/**
 * A segmented toggle for switching between dark, light, and system themes.
 * Uses the useTheme hook for persistence and system preference detection.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme()

  return (
    <div
      className={cn(
        'inline-flex items-center gap-0.5 rounded-lg p-1',
        'bg-surface-3/50 border border-subtle',
        className,
      )}
      role="radiogroup"
      aria-label="Theme selection"
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          role="radio"
          aria-checked={theme === opt.value}
          onClick={() => setTheme(opt.value)}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium',
            'transition-all duration-200',
            theme === opt.value
              ? 'bg-surface-4 text-primary shadow-xs'
              : 'text-tertiary hover:text-secondary',
          )}
          aria-label={`Switch to ${opt.label} theme`}
        >
          <opt.icon className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{opt.label}</span>
        </button>
      ))}
    </div>
  )
}
